

















































import Vue from 'vue';
import { AuthService, API, AUTH_API } from '@/services';
import { AxiosResponse, AxiosError } from 'axios';
import { Params, Strings } from '@/constants';
import {
  ErrorMessage,
  PageHeader,
  SearchForm,
  GroupDisplay,
  SignOutMessage,
} from '@/components';
import store from '@/store';
import { Hub } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import { HelperFunctions } from '@/helpers';

interface Data {
  AuthService: any;
  isLoading: boolean;
  unsortedGroups: Group[];
  hasError: boolean;
  searchHasOccured: boolean;
  authenticated: boolean;
  errorMessage: string;
  notAuthenticatedMessage: string;
  isFetchingJWT: boolean;
  userId: string;
  idp: string;
  isSearchByGroup: string;
  partnerId: string;
}

// The keys of this table must match the idp values coming back from
// further-authentication-api/v1-legacy/sessions/details/me
const IDP_TABLE = {
  further: 'FURTHER',
  BCCF: 'CAREFIRST',
  BCHZN: 'HORIZON',
  CBC: 'CAPBLUE',
};

const API_PATH = process.env.VUE_APP_API_PATH;
const API_VERSION = process.env.VUE_APP_API_VERSION;
const GROUP_API_PATH = process.env.VUE_APP_GROUP_PATH;

export default Vue.extend({
  name: 'Home',
  async mounted() {
    Hub.listen('auth', (data) => {
      const { payload } = data;
      if (payload.event === 'cognitoHostedUI') {
        Auth.currentAuthenticatedUser().then(async (user) => {
          const token = user.signInUserSession.idToken.jwtToken;
          const refreshToken = user.signInUserSession.refreshToken.token;
          AuthService.setCookie(token);
          AuthService.authorizeUser(token);
          localStorage.setItem('refreshToken', refreshToken);
          this.idp = await this.getCurrentUsersIDP();
        });
      }
    });
    await AuthService.checkForToken();
    if (!store.Auth.getters.oidcInProgress && !this.isSignedOut && store.Auth.getters.idToken) {
      this.idp = await this.getCurrentUsersIDP();
    }
  },
  data(): Data {
    return {
      AuthService,
      isLoading: false,
      unsortedGroups: [] as Group[],
      hasError: false,
      searchHasOccured: false,
      authenticated: false,
      errorMessage: Strings.DEFAULT_ERROR_MESSAGE,
      notAuthenticatedMessage: 'You are not authorized.',
      isFetchingJWT: false,
      userId: '',
      idp: IDP_TABLE.further,
      isSearchByGroup: 'group',
      partnerId: '',
    };
  },
  computed: {
    isAuthenticated(): boolean {
      return this.AuthService.isAuthenticated;
    },
    hasAuthError(): boolean {
      return store.Auth.getters.errorCode ? true : false;
    },
    isSignedOut(): boolean {
      return HelperFunctions.getUrlParameter(Params.SIGNED_OUT) ? true : false;
    },
  },
  methods: {
    performSearch({
      GroupSearchCriteria,
      GroupSearchBy,
      Role,
    }: SearchConfig): void {
      this.isLoading = true;
      this.hasError = false;

      let params = {};
      if (this.isSearchByGroup === 'group') {
        params = {
          [GroupSearchBy]: GroupSearchCriteria,
          role: Role,
          partnerId: this.idp,
        };
      } else {
        params = {
          [GroupSearchBy]: GroupSearchCriteria,
          partnerId: this.idp,
        };
      }

      API.get(`${API_PATH}/${API_VERSION}/users/keys`, {
        params,
      })
        .then((groups: AxiosResponse) => {
          this.unsortedGroups = groups.data;
          this.isLoading = false;
          this.searchHasOccured = true;
          if (this.unsortedGroups.length === 1) {
            this.selectGroup(String(this.unsortedGroups[0].userId));
          }
        })
        .catch((error: AxiosError) => {
          if (error.message) {
            this.errorMessage = error.message;
          } else {
            this.errorMessage = Strings.DEFAULT_ERROR_MESSAGE;
          }

          this.searchHasOccured = true;
          this.isLoading = false;
          this.hasError = true;
        });
    },
    selectGroup(userId: string) {
      this.userId = userId;
      this.isFetchingJWT = true;
      this.hasError = false;

      API.post(`${API_PATH}/${API_VERSION}/group/jwt`, {
        userId, refreshToken: localStorage.getItem('refreshToken'),
      })
        .then((response) => {
          this.isFetchingJWT = false;
          const REDIRECT_PROP = `VUE_APP_PORTAL_BASE_URI_${this.idp}`;
          const URL = process.env[REDIRECT_PROP];
          const JWT = response.data.value;
          window.open(`${URL}?id_token=${JWT}`);
        })
        .catch((error: AxiosError) => {
          if (error.message) {
            this.errorMessage = error.message;
          } else {
            this.errorMessage = Strings.DEFAULT_ERROR_MESSAGE;
          }
          this.isFetchingJWT = false;
          this.hasError = true;
        });
    },
    resetSearchResults(): void {
      this.unsortedGroups = [];
      this.searchHasOccured = false;
    },
    async getCurrentUsersIDP(): Promise<string> {
      try {
        const RESPONSE = await API.get(
          `${API_PATH}/${API_VERSION}/sessions/details/me`,
          {},
        );
        const IDP: string = RESPONSE.data.idp;
        return Promise.resolve(IDP_TABLE[IDP as keyof typeof IDP_TABLE]);
      } catch (e) {
        return Promise.resolve(IDP_TABLE.further);
      }
    },
  },
  components: {
    ErrorMessage,
    PageHeader,
    SearchForm,
    GroupDisplay,
    SignOutMessage,
  },
});
